// A value in a sentence: how the conflict panel and "reset to …" show a value without a control.

import type { DisplayUnit } from './layout';
import { isMoneySchema } from './money';
import { discriminatorOf, enumOf, propertiesOf, resolveSchema, variantsOf, type JsonSchema, type SchemaContext } from './schema';
import type { SettingLabels } from './types';

interface FormatOptions {
  readonly entryKey: string;
  readonly schema: JsonSchema;
  readonly context: SchemaContext;
  readonly labels: SettingLabels;
  readonly words: { readonly on: string; readonly off: string; readonly notSet: string; readonly items: (count: number) => string };
  /** The setting's display unit (`layout.ts`): 300 seconds reads "5 minutes", 1000 basis points "10%". */
  readonly display?: DisplayUnit | undefined;
  /** How a number reads in its unit (`units.ts` `formatWithUnit`). */
  readonly withUnit?: ((value: number, display: DisplayUnit) => string | undefined) | undefined;
}

export function formatValue(value: unknown, options: FormatOptions, path: readonly string[] = []): string {
  let { schema } = resolveSchema(options.schema, options.context);
  // A union of shapes reads as the shape the value is.
  const variants = variantsOf(schema, options.context);
  if (variants.length > 0 && typeof value === 'object' && value !== null) {
    const tag = discriminatorOf(variants);
    const record = value as Record<string, unknown>;
    schema = variants.find((variant) => tag !== undefined && propertiesOf(variant)[tag]?.['const'] === record[tag]) ?? schema;
  }
  if (value === undefined || value === null || value === '') return options.words.notSet;
  if (typeof value === 'boolean') return value ? options.words.on : options.words.off;
  // Money is integer minor units, read in the screen's currency: 10000 → "₹100.00".
  if (typeof value === 'number' && Number.isSafeInteger(value) && options.context.money !== undefined && isMoneySchema(schema)) {
    return options.context.money.format(value);
  }
  if (typeof value === 'number' && path.length === 0 && options.display !== undefined && options.withUnit !== undefined) {
    const inUnit = options.withUnit(value, options.display);
    if (inUnit !== undefined) return inUnit;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return enumOf(schema) === undefined && !('const' in schema) ? String(value) : options.labels.option(options.entryKey, path, value);
  }
  if (Array.isArray(value)) {
    const items = schema['items'];
    const itemSchema = typeof items === 'object' && items !== null ? resolveSchema(items as JsonSchema, options.context).schema : {};
    if (enumOf(itemSchema) !== undefined) {
      return value.map((item) => options.labels.option(options.entryKey, path, item as string)).join(', ') || options.words.notSet;
    }
    // A short list of plain values reads as itself: "10, 5, 1".
    if (value.length > 0 && value.length <= 6 && value.every((item) => typeof item === 'string' || typeof item === 'number')) return value.join(', ');
    return options.words.items(value.length);
  }
  if (typeof value === 'object') {
    const properties = propertiesOf(schema);
    const parts = Object.entries(value as Record<string, unknown>)
      .filter(([, inner]) => inner !== undefined && inner !== null && inner !== '')
      .map(([name, inner]) =>
        formatValue(inner, { ...options, schema: properties[name] ?? {} }, [...path, name]),
      );
    return parts.join(', ') || options.words.notSet;
  }
  return String(value);
}
